import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { usersApi, UserDto } from '../../services/usersService';
import { useAuth } from '../../contexts/AuthContext';

interface StaffPickerProps {
  /** מזהה העובד המשויך (0/undefined = ללא שיוך → ברירת מחדל: המשתמש הנוכחי) */
  value: number;
  onChange: (userId: number) => void;
  /** תווית מעל הבורר. מחרוזת ריקה ("") מסתירה את התווית (לשימוש inline). ברירת מחדל: "משויך ל" */
  label?: string;
  disabled?: boolean;
  className?: string;
}

// מטמון ברמת המודול — רשימת הצוות נטענת פעם אחת ומשותפת לכל מופעי הבורר בעמוד.
let staffCache: UserDto[] | null = null;
let staffPromise: Promise<UserDto[]> | null = null;

const loadStaffOnce = (): Promise<UserDto[]> => {
  if (staffCache) return Promise.resolve(staffCache);
  if (!staffPromise) {
    staffPromise = usersApi
      .getStaff()
      .then((list) => {
        staffCache = list;
        return list;
      })
      .catch((err) => {
        staffPromise = null; // מאפשר ניסיון טעינה חוזר לאחר כשל
        throw err;
      });
  }
  return staffPromise;
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  background: white;
  color: var(--text-primary);
  direction: rtl;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--primary-light);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: var(--background-secondary);
  }
`;

/**
 * בורר עובד משויך — רשימת הצוות (מנהלים + עובדים) לשיוך/שינוי-שיוך של פריטים מתוזמנים.
 * הרשימה בדרך כלל קטנה ולכן select רגיל מספיק. כאשר אין ערך — ברירת המחדל היא המשתמש הנוכחי.
 */
const StaffPicker: React.FC<StaffPickerProps> = ({
  value,
  onChange,
  label = 'משויך ל',
  disabled,
  className,
}) => {
  const { user } = useAuth();
  const [staff, setStaff] = useState<UserDto[]>(staffCache ?? []);

  useEffect(() => {
    let mounted = true;
    loadStaffOnce()
      .then((list) => {
        if (mounted) setStaff(list);
      })
      .catch(() => {
        /* שגיאת טעינה — הבורר יישאר עם הערך הנוכחי בלבד */
      });
    return () => {
      mounted = false;
    };
  }, []);

  const currentUserId = user?.id ?? 0;

  // ברירת מחדל: אם אין משויך — משייכים למשתמש הנוכחי (מסנכרן גם את ההורה).
  // onChange מושמט מהתלויות בכוונה כדי למנוע לולאת רינדור.
  useEffect(() => {
    if (!value && currentUserId) {
      onChange(currentUserId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, currentUserId]);

  const selectedValue = value || currentUserId;
  const hasSelected = staff.some((s) => s.id === selectedValue);

  return (
    <Wrapper className={className}>
      {label ? <Label>{label}</Label> : null}
      <Select
        value={selectedValue || ''}
        onChange={(e) => onChange(parseInt(e.target.value, 10) || 0)}
        disabled={disabled}
      >
        {!selectedValue && <option value="">בחר עובד...</option>}
        {/* גיבוי: אם המשויך הנוכחי אינו ברשימה (עדיין נטענת / משתמש לא פעיל) — עדיין נציג אותו */}
        {selectedValue > 0 && !hasSelected && (
          <option value={selectedValue}>
            {user && user.id === selectedValue ? user.fullName : `#${selectedValue}`}
          </option>
        )}
        {staff.map((s) => (
          <option key={s.id} value={s.id}>
            {s.fullName}
          </option>
        ))}
      </Select>
    </Wrapper>
  );
};

export default StaffPicker;
